
import React from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import MarkerManager from './MarkerManager';

const ClusterManager = ({
  clusters,
  mapRef,
  carStatus,
  vehicleSelected,
  clusterCounts,
  vehiclePositionsRef,
  markerRef,
  vehicleIconsRef,
  positionsInfoBox,
  detailedVehicleInfo,
  isLoadingDetail,
  handleMarkerClick
}) => {
  const visibleClusters = React.useMemo(() => {
    if (!mapRef || !clusters) return [];
    const bounds = mapRef.getBounds();
    return clusters.filter(cluster => {
      const [lng, lat] = cluster.geometry.coordinates;
      return bounds.contains([lat, lng]);
    });
  }, [clusters, mapRef]);

  const displayClusterIcon = (cluster) => {
    const count = clusterCounts.counts[cluster.properties.cluster_id] || 0;
    if (count <= 1) return null;

    let bg = '#1976d2';
    if (count < 10) bg = '#00ABE1CC';
    else if (count <= 50) bg = '#F9CF26CC';
    else bg = '#FF7E55CC';

    const size = count < 100 ? 40 : count < 1000 ? 50 : 60;
    return L.divIcon({
      html: `<div style="background:${bg};width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#212529;font-weight:bold;font-size:14px;">${count}</div>`,
      className: 'custom-cluster',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2]
    });
  };

  return (
    <>
      {visibleClusters.map((cluster) => {
        const [lng, lat] = cluster.geometry.coordinates;
        const { cluster: isCluster, cluster_id, vehicle } = cluster.properties;
        const position = [lat, lng];

        if (isCluster) {
          const count = clusterCounts.counts[cluster_id] || 0;
          if (count === 1) {
            const single = clusterCounts.singleVehicles[cluster_id];
            if (!single) return null;

            const isVisible = !carStatus || carStatus === single.device_status;
            const pos = vehiclePositionsRef.current[single.device_id] || [single.lat, single.lng];
            const inView = mapRef?.getBounds().contains(pos);

            return (
              <MarkerManager
                key={single.device_id}
                vehicle={single}
                position={pos}
                isSelected={false}
                isInViewport={inView}
                onClick={handleMarkerClick}
                markerRef={markerRef}
                vehicleIconsRef={vehicleIconsRef}
                positionsInfoBox={positionsInfoBox}
                detailedVehicleInfo={detailedVehicleInfo}
                isLoadingDetail={isLoadingDetail}
                opacity={isVisible ? 1 : 0}
                mapRef={mapRef}
              />
            );
          }

          const icon = displayClusterIcon(cluster);
          if (!icon) return null;

          return (
            <Marker
              key={`cluster-${cluster_id}`}
              position={position}
              icon={icon}
              eventHandlers={{
                click: () => mapRef.flyTo(position, mapRef.getZoom() + 2, { animate: true })
              }}
            />
          );
        }

        if (!vehicle || vehicleSelected.device_id === vehicle.device_id) return null;

        const isVisible = !carStatus || carStatus === vehicle.device_status;
        const pos = vehiclePositionsRef.current[vehicle.device_id] || [vehicle.lat, vehicle.lng];
        const inView = mapRef?.getBounds().contains(pos);

        return (
          <MarkerManager
            key={vehicle.device_id}
            vehicle={vehicle}
            position={pos}
            isSelected={false}
            isInViewport={inView}
            onClick={handleMarkerClick}
            markerRef={markerRef}
            vehicleIconsRef={vehicleIconsRef}
            positionsInfoBox={positionsInfoBox}
            detailedVehicleInfo={detailedVehicleInfo}
            isLoadingDetail={isLoadingDetail}
            opacity={isVisible ? 1 : 0}
            mapRef={mapRef}
          />
        );
      })}
    </>
  );
};

export default React.memo(ClusterManager);